import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../atoms/Button/Button';
import { Heading } from '../atoms/Typography/Heading';
import { ImageSocialPanel } from './ImageSocialPanel';
import { useTranslation } from '../../i18n/useTranslation';
import './ImageSocialPanel.css';

/**
 * "Sharing & comments" for the full-screen ImageViewer: a button that opens a
 * side sheet with the image's visibility, share link, likes and comments.
 * Kept out of ImageViewer itself so the viewer only renders one element.
 */
export function ImageViewerSocial({ imageId }: { imageId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const headingId = useId();
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  // A different image closes the sheet.
  useEffect(() => setOpen(false), [imageId]);

  useEffect(() => {
    if (open) sheetRef.current?.focus();
  }, [open]);

  function close(): void {
    setOpen(false);
    toggleRef.current?.focus();
  }

  return (
    <>
      <Button
        ref={toggleRef}
        type="button"
        size="sm"
        variant="secondary"
        className="rg-image-social-toggle-button"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        data-testid="image-social-toggle"
      >
        {t('imageSocial.open')}
      </Button>
      {open && (
        <div
          ref={sheetRef}
          className="rg-image-social-sheet"
          role="region"
          aria-labelledby={headingId}
          tabIndex={-1}
          data-testid="image-social-sheet"
        >
          <div className="rg-image-social-sheet__head">
            <Heading level={2} variant="subsection" id={headingId}>{t('imageSocial.title')}</Heading>
            <Button type="button" size="sm" variant="ghost" onClick={close}>
              {t('imageSocial.close')}
            </Button>
          </div>
          <ImageSocialPanel imageId={imageId} headingLevel={3} />
        </div>
      )}
    </>
  );
}

export default ImageViewerSocial;
